import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link as LinkIcon, SignIn, X, IdentificationCard } from "@phosphor-icons/react";

// Admin-only card to manage a "linked driver" session: connect once with a
// driver's credentials, then jump in/out of the driver dashboard without
// touching the admin session. Designed to live in the AdminDashboard header.
export default function LinkedDriverCard() {
  const navigate = useNavigate();
  const { linkedDriver, linkDriver, unlinkDriver, enterDriverMode } = useAuth();
  const [openLink, setOpenLink] = useState(false);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const onLink = async (e) => {
    e?.preventDefault();
    if (!creds.email.trim() || !creds.password) {
      toast.error("Email et mot de passe requis"); return;
    }
    setBusy(true);
    try {
      await linkDriver(creds.email.trim().toLowerCase(), creds.password);
      toast.success("Compte chauffeur lié");
      setOpenLink(false);
      setCreds({ email: "", password: "" });
    } catch (err) {
      const reason = err.reason || err.response?.data?.detail;
      toast.error(reason === "not_driver" ? "Ce compte n'est pas un compte chauffeur" : (reason || "Identifiants invalides"));
    } finally { setBusy(false); }
  };

  const onEnter = async () => {
    try {
      await enterDriverMode();
      navigate("/driver");
    } catch {
      toast.error("Aucun compte chauffeur lié");
    }
  };

  const onUnlink = () => {
    if (!window.confirm("Dissocier le compte chauffeur ? Vous devrez vous reconnecter pour le relier à nouveau.")) return;
    unlinkDriver();
    toast.success("Compte chauffeur dissocié");
  };

  if (!linkedDriver) {
    return (
      <>
        <Button data-testid="link-driver-open" variant="outline" size="sm"
          onClick={() => setOpenLink(true)}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-9">
          <LinkIcon size={14} className="mr-1.5" /> Lier un compte chauffeur
        </Button>
        <LinkModal open={openLink} onClose={() => setOpenLink(false)} creds={creds} setCreds={setCreds} onSubmit={onLink} busy={busy} />
      </>
    );
  }

  return (
    <div data-testid="linked-driver-card" className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5">
      <IdentificationCard size={14} weight="bold" className="text-emerald-400 shrink-0" />
      <span className="text-xs text-emerald-200 truncate max-w-[140px]" title={linkedDriver.name || linkedDriver.email}>
        {linkedDriver.name || linkedDriver.email}
      </span>
      <button data-testid="link-driver-enter" onClick={onEnter}
        className="text-[11px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-500 text-black font-semibold hover:bg-emerald-400">
        <SignIn size={11} weight="bold" /> Accéder
      </button>
      <button data-testid="link-driver-unlink" onClick={onUnlink}
        title="Dissocier le compte chauffeur"
        className="text-emerald-300/80 hover:text-rose-400 p-0.5">
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

function LinkModal({ open, onClose, creds, setCreds, onSubmit, busy }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="link-driver-modal" className="bg-[#121212] border-zinc-800 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle>Lier un compte chauffeur</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Connectez le compte chauffeur une seule fois. Vous pourrez ensuite y accéder sans saisir le mot de passe à nouveau.
            Votre session admin reste active en parallèle.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email du chauffeur</Label>
            <Input data-testid="link-driver-email" type="email" autoFocus
              value={creds.email} onChange={(e) => setCreds((p) => ({ ...p, email: e.target.value }))}
              className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
          </div>
          <div className="space-y-1">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Mot de passe</Label>
            <Input data-testid="link-driver-password" type="password"
              value={creds.password} onChange={(e) => setCreds((p) => ({ ...p, password: e.target.value }))}
              className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
          </div>
          <Button data-testid="link-driver-submit" type="submit" disabled={busy}
            className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-10">
            {busy ? "Connexion…" : "Lier le compte"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
